import type { Ref } from 'vue'
import type { SdPerformanceScope } from '@/types/salesDashboardSession'
import type {
  SalesDashboardCustomRange,
  SalesDashboardFilters,
  SalesMode,
  SalesTimeRangePreset,
} from '@/types/salesDashboardShared'
import {
  appendSalesDashboardFiltersToParams,
  appendSalesTimeRangeToParams,
  type SalesDashboardFilterPage,
} from '@/utils/salesDashboardSharedFilters'
import type { useSalesDashboardPagesStore } from '@/stores/salesDashboardPages'

export type DashboardPagesQueryParams = Record<string, string | number | string[] | undefined>

export type SalesDashboardPagesStore = ReturnType<typeof useSalesDashboardPagesStore>

export function serializeDashboardPagesQueryParams(params: DashboardPagesQueryParams): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && String(item) !== '') {
          search.append(`${key}[]`, String(item))
        }
      }
    } else {
      search.append(key, String(value))
    }
  }
  return search.toString()
}

function appendListingPeriodFromStore(
  params: DashboardPagesQueryParams,
  store: SalesDashboardPagesStore
): void {
  params.period = store.timeRange
  if (store.timeRange === 'custom') {
    if (store.customRange.from) params.start_date = store.customRange.from
    if (store.customRange.to) params.end_date = store.customRange.to
  }
}

export function buildLeaderboardParams(
  store: SalesDashboardPagesStore,
  pageSlug?: string
): DashboardPagesQueryParams {
  const params: DashboardPagesQueryParams = {
    source: pageSlug === 'texas-sales-by-rep' ? 'created' : store.salesMode,
  }
  appendListingPeriodFromStore(params, store)
  appendSalesDashboardFiltersToParams(params, store.activeFilters, {
    page: 'leaderboard',
    salesMode: store.salesMode,
    salesReps: store.filterOptions?.salesReps,
  })
  return params
}

export function buildPerformanceParams(
  store: SalesDashboardPagesStore,
  scope: SdPerformanceScope
): DashboardPagesQueryParams {
  const params: DashboardPagesQueryParams = {
    scope,
  }
  appendSalesTimeRangeToParams(
    params,
    store.timeRange,
    store.customRange,
    store.selectedMonth
  )
  appendSalesDashboardFiltersToParams(params, store.activeFilters, {
    page: 'performance',
  })
  return params
}

export function buildPerformanceExportParams(
  store: SalesDashboardPagesStore,
  scope: SdPerformanceScope
): DashboardPagesQueryParams {
  return {
    ...buildPerformanceParams(store, scope),
    source: store.salesMode,
  }
}

export function buildSaleDetailParams(
  store: SalesDashboardPagesStore,
  pageSlug?: string
): DashboardPagesQueryParams {
  const params: DashboardPagesQueryParams = {
    source: store.salesMode,
  }
  if (store.activeFilters.accountGroups.length > 0) {
    params.account_groups = [...store.activeFilters.accountGroups]
  }
  appendListingPeriodFromStore(params, store)
  appendSalesDashboardFiltersToParams(params, store.activeFilters, {
    page: 'sale-detail',
    salesMode: store.salesMode,
    salesReps: store.filterOptions?.salesReps,
  })
  return params
}

/** Build query params for GET /amazon-dashboard (source, period, status/GL/MIS only). */
export function buildAmazonDashboardParams(store: SalesDashboardPagesStore): DashboardPagesQueryParams {
  const params: DashboardPagesQueryParams = {
    source: store.salesMode,
  }
  appendListingPeriodFromStore(params, store)
  appendSalesDashboardFiltersToParams(params, store.activeFilters, {
    page: 'amazon-dashboard',
    salesMode: store.salesMode,
  })
  return params
}

export function buildOpenPipelineParams(
  store: SalesDashboardPagesStore,
  pipelineId: string
): DashboardPagesQueryParams {
  const params: DashboardPagesQueryParams = {
    pipeline_id: pipelineId,
  }
  appendSalesDashboardFiltersToParams(params, store.activeFilters, {
    page: 'open-pipeline',
  })
  return params
}

export function isApiBackedDashboardPage(pageSlug: string): boolean {
  return (
    pageSlug === 'performance' ||
    pageSlug === 'leaderboard' ||
    pageSlug === 'sale-detail' ||
    pageSlug === 'amazon-dashboard' ||
    pageSlug === 'texas-sales-by-rep' ||
    pageSlug === 'open-pipeline'
  )
}

export function resolveDashboardPagesApiKind(
  pageSlug: string
): 'leaderboard' | 'performance' | 'sale-detail' | 'amazon-dashboard' | 'open-pipeline' | null {
  if (pageSlug === 'leaderboard' || pageSlug === 'texas-sales-by-rep') return 'leaderboard'
  if (pageSlug === 'performance') return 'performance'
  if (pageSlug === 'sale-detail') return 'sale-detail'
  if (pageSlug === 'amazon-dashboard') return 'amazon-dashboard'
  if (pageSlug === 'open-pipeline') return 'open-pipeline'
  return null
}

export type { SalesDashboardFilters, SalesMode, SalesDashboardFilterPage, SalesTimeRangePreset }
